import { useState, useMemo, useEffect } from "react";
import {
  X,
  Palette,
  Link2,
  Unlink,
  CheckSquare,
  Square,
  Unlink2,
} from "lucide-react";
import { useConfigStore } from "../../store/configStore";
import { useEditorStore } from "../../store/editorStore";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { SkinThumbnail } from "../../components/SkinThumbnail";
import { cn } from "@/lib/utils";

export function ToningDetailPanel() {
  const { templateConfig } = useConfigStore();
  const {
    updateToning,
    deleteToning,
    addSkuToning,
    removeSkuToning,
    editingToningId,
    setEditingToningId,
  } = useEditorStore();

  const toning = templateConfig?.tonings.find((t) => t.id === editingToningId);
  const skus = templateConfig?.skus || {};

  const [name, setName] = useState(toning?.name || "");
  const [proValue, setProValue] = useState(toning?.pro?.toString() || "0");

  // 当 toning 变化时更新本地状态
  useEffect(() => {
    if (toning) {
      setName(toning.name || "");
      setProValue(toning.pro?.toString() || "0");
    }
  }, [toning]);

  // 获取所有 SKU 及其绑定状态（从 skus 获取完整数据）
  const allSkus = useMemo(() => {
    if (!templateConfig) return [];
    return templateConfig.selection.flatMap((sel: any) => {
      const skuIds = sel.skuIds || [];
      return skuIds
        .map((skuId: string) => {
          const sku = skus[skuId] || {};
          return {
            id: skuId,
            skinName: sku.skinName,
            category: sku.category,
            selectionId: sel.id,
            selectionName: sel.name || sel.id,
            isBound: sku.toningIds?.includes(editingToningId || "") || false,
          };
        })
        .filter((sku: any) => sku.skinName);
    });
  }, [templateConfig, skus, editingToningId]);

  // 已绑定的 SKU
  const boundSkus = allSkus.filter((sku) => sku.isBound);

  // 未绑定的 SKU，按 Selection 分组
  const unboundSkusBySelection = useMemo(() => {
    const unboundSkus = allSkus.filter((sku) => !sku.isBound);
    const grouped: Record<string, typeof unboundSkus> = {};
    unboundSkus.forEach((sku) => {
      if (!grouped[sku.selectionId]) {
        grouped[sku.selectionId] = [];
      }
      grouped[sku.selectionId].push(sku);
    });
    return grouped;
  }, [allSkus]);

  // 已绑定的 SKU，按 Selection 分组
  const boundSkusBySelection = useMemo(() => {
    const grouped: Record<string, typeof boundSkus> = {};
    boundSkus.forEach((sku) => {
      if (!grouped[sku.selectionId]) {
        grouped[sku.selectionId] = [];
      }
      grouped[sku.selectionId].push(sku);
    });
    return grouped;
  }, [boundSkus]);

  // 检查是否绑定了多个 category 的 SKU（可能产生染色冲突）
  const boundCategories = useMemo(() => {
    const categories = new Set(
      boundSkus.map((sku) => sku.category).filter(Boolean)
    );
    return Array.from(categories);
  }, [boundSkus]);
  const hasMultipleCategories = boundCategories.length > 1;

  if (!toning) {
    return (
      <Card className="h-full flex flex-col">
        <CardHeader className="pb-2 flex-shrink-0">
          <CardTitle className="text-lg">调色详情</CardTitle>
        </CardHeader>
        <CardContent className="flex-1 flex items-center justify-center">
          <p className="text-sm text-muted-foreground text-center">
            点击调色方案的「详情」按钮查看
          </p>
        </CardContent>
      </Card>
    );
  }

  const handleNameBlur = () => {
    if (name !== toning.name) {
      updateToning(toning.id, { name });
    }
  };

  const handleProChange = (value: string) => {
    setProValue(value);
    const num = parseInt(value);
    if (!isNaN(num) && num >= 0) {
      updateToning(toning.id, { pro: num });
    }
  };

  const handleDelete = () => {
    deleteToning(toning.id);
    setEditingToningId(null);
  };

  const handleBindSku = (skinName: string) => {
    addSkuToning(skinName, toning.id);
  };

  const handleUnbindSku = (skinName: string) => {
    removeSkuToning(skinName, toning.id);
  };

  // 批量绑定某个 Selection 下所有未绑定的 SKU
  const handleBindSelection = (selectionId: string) => {
    const skus = unboundSkusBySelection[selectionId];
    if (!skus?.length) return;
    skus.forEach((sku: any) => addSkuToning(sku.skinName, toning.id));
  };

  // 批量解绑某个 Selection 下所有已绑定的 SKU
  const handleUnbindSelection = (selectionId: string) => {
    const skus = boundSkusBySelection[selectionId];
    if (!skus?.length) return;
    skus.forEach((sku: any) => removeSkuToning(sku.skinName, toning.id));
  };

  return (
    <Card className="h-full flex flex-col">
      <CardHeader className="pb-2 flex-shrink-0">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Palette className="h-4 w-4 text-primary" />
            调色方案详情
          </CardTitle>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={() => setEditingToningId(null)}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="flex-1 overflow-auto space-y-4">
        {/* 名称编辑 */}
        <div className="space-y-1.5">
          <Label className="text-xs">方案名称</Label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={handleNameBlur}
            placeholder="调色方案名称"
            className="text-sm"
          />
        </div>

        {/* ID */}
        <div className="space-y-1.5">
          <Label className="text-xs">方案 ID</Label>
          <Input
            value={toning.id}
            disabled
            className="font-mono text-sm bg-muted"
          />
        </div>

        {/* 价格 */}
        <div className="space-y-2">
          <Label className="text-xs">价格 (pro)</Label>
          <div className="flex items-center gap-2">
            <Input
              type="number"
              min={0}
              value={proValue}
              onChange={(e) => handleProChange(e.target.value)}
              className="text-sm"
              placeholder="0 = 免费"
            />
            <Badge
              className={cn(
                "shrink-0",
                parseInt(proValue) > 0 ? "bg-amber-500" : ""
              )}
            >
              {parseInt(proValue) > 0 ? `¥${proValue}` : "免费"}
            </Badge>
          </div>
          {/* 快捷价格按钮 */}
          <div className="flex flex-wrap gap-1.5">
            {[0, 100, 200, 500].map((price) => (
              <Button
                key={price}
                variant={parseInt(proValue) === price ? "default" : "outline"}
                size="sm"
                className={cn(
                  "h-6 px-2 text-xs",
                  parseInt(proValue) === price &&
                    price > 0 &&
                    "bg-amber-500 hover:bg-amber-600"
                )}
                onClick={() => handleProChange(price.toString())}
              >
                {price === 0 ? "免费" : `¥${price}`}
              </Button>
            ))}
          </div>
        </div>

        {/* 统计 */}
        <div className="flex items-center gap-2 flex-wrap">
          <Badge variant="outline">{toning.colors.length} 种配色</Badge>
          <Badge variant="secondary">{boundSkus.length} 个 SKU 已绑定</Badge>
          {hasMultipleCategories && (
            <Badge variant="destructive" className="text-[10px]">
              ⚠️ 跨 {boundCategories.length} 个分类
            </Badge>
          )}
        </div>
        {hasMultipleCategories && (
          <div className="text-[10px] text-amber-600 bg-amber-50 dark:bg-amber-950/20 px-2 py-1.5 rounded">
            ⚠️ 此方案绑定了多个分类的 SKU（{boundCategories.join("、")}
            ），可能产生染色冲突。 同一 SKU 只会应用一个染色方案。
          </div>
        )}

        {/* 已绑定的 SKU - 按 Selection 分组 */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Link2 className="h-3.5 w-3.5 text-primary" />
            <Label className="text-xs">已绑定 SKU</Label>
          </div>

          {boundSkus.length === 0 ? (
            <div className="text-center text-muted-foreground text-xs py-3 bg-muted/30 rounded-lg">
              暂无绑定的 SKU
            </div>
          ) : (
            <ScrollArea className="h-[140px]">
              <div className="space-y-3 pr-3">
                {Object.entries(boundSkusBySelection).map(
                  ([selectionId, skus]) => (
                    <div key={selectionId} className="space-y-1.5">
                      {/* Selection 标题带全选按钮 */}
                      <div className="flex items-center justify-between px-1">
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">
                            {skus[0]?.selectionName || selectionId}
                          </span>
                          <Badge
                            variant="outline"
                            className="h-4 px-1 text-[9px]"
                          >
                            {skus.length}
                          </Badge>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-5 px-1.5 text-[10px] text-destructive hover:text-destructive"
                          onClick={() => handleUnbindSelection(selectionId)}
                        >
                          <Unlink className="h-2.5 w-2.5 mr-0.5" />
                          全部解绑
                        </Button>
                      </div>
                      {/* SKU 网格 */}
                      <div className="grid grid-cols-4 gap-1.5">
                        {skus.map((sku) => (
                          <div
                            key={sku.id}
                            className="relative p-1 rounded-lg bg-primary/10 border border-primary/30"
                          >
                            <div className="flex justify-center mb-0.5">
                              <SkinThumbnail
                                skinName={sku.skinName}
                                size={36}
                              />
                            </div>
                            <p className="text-[9px] font-mono text-center truncate text-muted-foreground">
                              {sku.id.slice(0, 6)}
                            </p>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="absolute top-0 right-0 h-4 w-4 text-destructive hover:text-destructive opacity-0 hover:opacity-100"
                              onClick={() => handleUnbindSku(sku.skinName)}
                            >
                              <X className="h-2.5 w-2.5" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )
                )}
              </div>
            </ScrollArea>
          )}
        </div>

        {/* 可绑定的 SKU - 按 Selection 分组，支持全选 */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Label className="text-xs text-muted-foreground">可绑定 SKU</Label>
          </div>

          {Object.keys(unboundSkusBySelection).length === 0 ? (
            <div className="text-center text-muted-foreground text-xs py-3 bg-muted/30 rounded-lg">
              所有 SKU 都已绑定此调色方案
            </div>
          ) : (
            <ScrollArea className="h-[200px]">
              <div className="space-y-3 pr-3">
                {Object.entries(unboundSkusBySelection).map(
                  ([selectionId, skus]) => (
                    <div key={selectionId} className="space-y-1.5">
                      {/* Selection 标题带全选按钮 */}
                      <div className="flex items-center justify-between px-1">
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">
                            {skus[0]?.selectionName || selectionId}
                          </span>
                          <Badge
                            variant="outline"
                            className="h-4 px-1 text-[9px]"
                          >
                            {skus.length}
                          </Badge>
                        </div>
                        <Button
                          variant="default"
                          size="sm"
                          className="h-5 px-1.5 text-[10px]"
                          onClick={() => handleBindSelection(selectionId)}
                        >
                          <CheckSquare className="h-2.5 w-2.5 mr-0.5" />
                          全选
                        </Button>
                      </div>
                      {/* SKU 网格 */}
                      <div className="grid grid-cols-4 gap-1.5">
                        {skus.map((sku) => (
                          <div
                            key={sku.id}
                            className="relative p-1 rounded-lg bg-muted/30 hover:bg-muted/50 cursor-pointer transition-colors"
                            onClick={() => handleBindSku(sku.skinName)}
                          >
                            <div className="flex justify-center mb-0.5">
                              <SkinThumbnail
                                skinName={sku.skinName}
                                size={36}
                              />
                            </div>
                            <p className="text-[9px] font-mono text-center truncate text-muted-foreground">
                              {sku.id.slice(0, 6)}
                            </p>
                            <div className="absolute top-0.5 right-0.5 opacity-50">
                              <Square className="h-2.5 w-2.5 text-muted-foreground" />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )
                )}
              </div>
            </ScrollArea>
          )}
        </div>

        {/* 从模板中移除按钮 */}
        <Button
          variant="outline"
          size="sm"
          className="w-full text-destructive hover:text-destructive"
          onClick={handleDelete}
          disabled={boundSkus.length > 0}
        >
          <Unlink2 className="h-3 w-3 mr-1" />
          {boundSkus.length > 0
            ? `请先解绑 ${boundSkus.length} 个 SKU`
            : "从模板中移除此调色方案"}
        </Button>
      </CardContent>
    </Card>
  );
}
